import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "tecnico" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(form);
      toast.success("Cuenta creada");
      nav("/", { replace: true });
    } catch (err) {
      toast.error(formatErr(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[var(--legatum-bg)]">
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-sm"
        data-testid="register-form"
      >
        <div className="mb-8 flex items-center gap-3">
          <div className="h-9 w-9 rounded-md flex items-center justify-center"
               style={{ background: "linear-gradient(135deg,#C9A24A,#8a6f2e)" }}>
            <span className="text-[#0b0b0b] font-bold tracking-wider">L</span>
          </div>
          <div className="text-sm font-semibold tracking-[0.3em]">LEGATUM CAPITAL</div>
        </div>
        <h2 className="text-3xl font-semibold tracking-tight">Crear cuenta</h2>
        <p className="mt-2 text-sm text-[var(--legatum-text-secondary)]">Acceso a la plataforma corporativa.</p>

        <div className="mt-8 space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-widest text-[var(--legatum-text-muted)]">Nombre</Label>
            <Input data-testid="register-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 bg-[var(--legatum-elevated)] h-11" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[var(--legatum-text-muted)]">Email</Label>
            <Input data-testid="register-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2 bg-[var(--legatum-elevated)] h-11" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[var(--legatum-text-muted)]">Contraseña</Label>
            <Input data-testid="register-password-input" type="password" minLength={6} required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-2 bg-[var(--legatum-elevated)] h-11" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-widest text-[var(--legatum-text-muted)]">Rol</Label>
            <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
              <SelectTrigger data-testid="register-role-select" className="mt-2 bg-[var(--legatum-elevated)] h-11">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="admin">Administrador</SelectItem>
                <SelectItem value="gestor">Gestor</SelectItem>
                <SelectItem value="tecnico">Técnico</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="register-submit-button" disabled={loading} className="w-full h-11 mt-2 font-bold text-[#0b0b0b] border-0 shadow-lg hover:shadow-xl transition-shadow" style={{ background: "linear-gradient(135deg,#C9A24A,#8a6f2e)" }}>
            {loading ? "Creando…" : "Crear cuenta"}
          </Button>
        </div>
        <div className="mt-6 text-center text-xs text-[var(--legatum-text-muted)]">
          ¿Ya tienes cuenta? <Link to="/login" className="text-[var(--legatum-gold)] hover:underline">Iniciar sesión</Link>
        </div>
        <Toaster theme="dark" position="bottom-right" />
      </motion.form>
    </div>
  );
}
